import React, { Dispatch, SetStateAction, useEffect, useState } from "react"
import { locationsSelector } from "@/atoms/userAtom"
import AddLocationModal from "@/modules/profile/LocationSettings/components/AddLocationModal"
import EditLocationModal from "@/modules/profile/LocationSettings/components/EditLocationModal"
import { PickupDetailsType } from "@/types/createSingleOrder.types"
import { Location, LocationDetailsType } from "@/types/orders.types"
import SearchFilter from "@/UI/Filters/SearchFilter"
import { getDisplayAddress } from "@/utils/addresses"
import { matchField } from "@/utils/compareObjects"
import { faCirclePlus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { message, Popover, Tabs, Divider, Space } from "antd"
import { useRecoilValue } from "recoil"
import { useTranslation } from "react-i18next"

interface PickupSelectPopoverProps {
  children: React.ReactElement
  onSelect?: (locationId?: string, orderId?: string) => Promise<void>
  id?: string
  sender?: Location
  setSelectedStore?: Dispatch<SetStateAction<PickupDetailsType | undefined>>
}

const PickupSelectPopover: React.FC<PickupSelectPopoverProps> = ({
  children,
  onSelect,
  id,
  sender,
  setSelectedStore,
}) => {
  const { t } = useTranslation(["common"])
  const stores = useRecoilValue(locationsSelector)
  const [open, setOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [searchValue, setSearchValue] = useState("")
  const [filteredStores, setFilteredStores] = useState<PickupDetailsType[]>([])

  // Work around antd v5.8.2 hover and click conflicting on popovers
  const [modalOpen, setModalOpen] = React.useState(false)
  const [editModalOpen, setEditModalOpen] = React.useState<string>("")
  const onOpenChange = (nextOpen: boolean) => {
    if ((modalOpen || editModalOpen) && !nextOpen) {
      return
    }

    setOpen(nextOpen)
  }
  const handleSavePickup = async (storeId?: string) => {
    try {
      setLoading(true)
      onSelect && (await onSelect(storeId, id))
      if (!id) {
        setOpen(false)
        return
      }
    } catch (error) {
      message.error(t("common.notification.somethingWentWrongDetails"))
    } finally {
      setLoading(false)
      setOpen(false)
    }
  }

  const handleClearPickup = async () => {
    try {
      setLoading(true)
      onSelect && (await onSelect(undefined, id))
      if (!id) {
        setOpen(false)
        return
      }
    } catch (error) {
      message.error(t("common.notification.somethingWentWrongDetails"))
    } finally {
      setLoading(false)
      setOpen(false)
    }
  }

  useEffect(() => {
    if (open) {
      setFilteredStores(
        stores?.filter(
          (store) =>
            !searchValue ||
            matchField(store?.name, searchValue) ||
            matchField(store?.address, searchValue) ||
            matchField(store?.alias, searchValue) ||
            matchField(store?.postalCode as string, searchValue)
        )
      )
    }
  }, [searchValue, stores, open])

  return (
    <Popover
      zIndex={1000}
      placement="bottomRight"
      trigger={["click"]}
      title={
        <>
          {open && (
            <div
              className={`relative pr-2 ${loading && "opacity-50"}`}
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              <Tabs
                defaultActiveKey={!sender?.location?.id && sender?.location?.address ? "2" : "1"}
                items={[
                  {
                    label: <span className="!font-normal">{t("common.pickupLocations")}</span>,
                    key: "1",
                    children: (
                      <>
                        <div className="flex items-center pb-2 space-x-5">
                          <div className="flex flex-grow">
                            <SearchFilter
                              searchValue={searchValue}
                              handleSearchValue={(value) => setSearchValue(value)}
                              size="middle"
                              searchWidth="100%"
                            />
                          </div>
                          <p
                            onClick={() => {
                              handleClearPickup()
                              if (setSelectedStore) {
                                setSelectedStore(undefined)
                              }
                            }}
                            className="cursor-pointer hover:underline hover:text-kosmoPrimaryBlue"
                          >
                            {t("common.clear")}
                          </p>
                        </div>
                        <div className="h-[269px] overflow-y-auto overflow-x-hidden">
                          {filteredStores?.map((store) => {
                            return (
                              <div
                                key={store.id}
                                className={`flex mb-1 cursor-pointer rounded-lg border py-3 px-3 gap-x-2 pointer-events-auto ${
                                  sender?.location?.id === store.id ? "card-selected" : ""
                                } w-[300px]`}
                                onClick={() => {
                                  handleSavePickup(store.id)
                                  if (setSelectedStore) {
                                    setSelectedStore(store)
                                  }
                                }}
                              >
                                <div className="flex flex-col w-[88%]">
                                  <div className="pointer-events-none items-center text-sm w-full">
                                    {store?.alias && (
                                      <p className="whitespace-nowrap font-semibold">
                                        {store.alias}
                                      </p>
                                    )}
                                    {store?.name && (
                                      <p className="whitespace-nowrap">{store.name}</p>
                                    )}
                                    <p className="text-gray-500 whitespace-nowrap text-ellipsis overflow-hidden">
                                      {getDisplayAddress({
                                        address: store.address,
                                        buildingNumber: store.buildingNumber,
                                      } as LocationDetailsType)}
                                    </p>
                                  </div>
                                </div>
                                <div
                                  className="w-[10%] items-end"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                  }}
                                >
                                  <EditLocationModal
                                    store={store}
                                    iconSize="small"
                                    parentModalOpenControl={editModalOpen}
                                    setParentModalOpenControl={setEditModalOpen}
                                  />
                                </div>
                              </div>
                            )
                          })}
                        </div>
                        <Divider style={{ margin: "8px 0" }} />
                        <Space style={{ padding: "0 8px 4px" }}>
                          <AddLocationModal
                            parentModalOpenControl={modalOpen}
                            setParentModalOpenControl={setModalOpen}
                            button={
                              <div className="flex cursor-pointer">
                                <FontAwesomeIcon
                                  className="flex flex-shrink-0 h-3 mx-auto my-auto text-kosmoBlue ml-1 mr-2"
                                  icon={faCirclePlus}
                                />
                                <p> {t("profile.AddStoreModal.addNewLocation")}</p>
                              </div>
                            }
                          />
                        </Space>
                      </>
                    ),
                  },
                ]}
              />
            </div>
          )}
        </>
      }
      open={open}
      onOpenChange={onOpenChange}
      showArrow={false}
      destroyTooltipOnHide={true}
    >
      <div onClick={() => setOpen(true)}>{children}</div>
    </Popover>
  )
}

export default PickupSelectPopover
